import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { $createTextNode } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import OsScroll from '../ui/OsScroll';

class MemberOption extends MenuOption {
  constructor(member, label) {
    super(String(member.id));
    this.member = member;
    this.label = label;
  }
}

/**
 * "@" mentions inside the rich comment composer (card 7de79076) — the same
 * contract the textarea had: the picked member is recorded as {id, token} at
 * WRITE time (commentMentions.resolveMentions), and the token stays plain
 * text "@Name" in the comment, so a body re-read later never decides who was
 * notified. Built on Lexical's typeahead like SlashCommandPlugin.
 *
 * `onOpenChange(open)` lets the host keep Escape for the menu instead of
 * closing the whole task window.
 */
export default function CommentMentionPlugin({ members, labelOf, onPicked, onOpenChange }) {
  const [editor] = useLexicalComposerContext();
  const [query, setQuery] = useState(null);
  const triggerFn = useBasicTypeaheadTriggerMatch('@', { minLength: 0 });

  const options = useMemo(() => {
    if (query == null) return [];
    const q = query.toLowerCase();
    return (members || [])
      .filter((m) => labelOf(m).toLowerCase().includes(q))
      .slice(0, 6)
      .map((m) => new MemberOption(m, labelOf(m)));
  }, [members, labelOf, query]);

  const open = query != null && options.length > 0;
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const onSelectOption = useCallback(
    (option, nodeToReplace, closeMenu) => {
      const token = `@${option.label}`;
      editor.update(() => {
        const text = $createTextNode(`${token} `);
        if (nodeToReplace) nodeToReplace.replace(text);
        text.select();
      });
      onPicked({ id: String(option.member.id), token });
      closeMenu();
    },
    [editor, onPicked]
  );

  return (
    <LexicalTypeaheadMenuPlugin
      options={options}
      triggerFn={triggerFn}
      anchorClassName="lx-typeahead-anchor"
      onQueryChange={(q) => setQuery(q)}
      onSelectOption={onSelectOption}
      menuRenderFn={(anchorRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
        anchorRef.current && options.length
          ? createPortal(
              <div className="task-detail-mention-menu" role="listbox" aria-label="Mention a teammate">
                {/* The app's scrollbar, not the native one (owner, 14.10.2026). */}
                <OsScroll className="task-detail-mention-scroll">
                  {options.map((option, i) => (
                    <button
                      key={option.key}
                      ref={option.setRefElement}
                      type="button"
                      role="option"
                      aria-selected={selectedIndex === i}
                      className={`task-detail-mention-option${selectedIndex === i ? ' task-detail-mention-option--active' : ''}`}
                      onMouseEnter={() => setHighlightedIndex(i)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectOptionAndCleanUp(option)}
                    >
                      <span className="task-detail-mention-option-name">{option.label}</span>
                      {option.member.email && option.member.name && (
                        <span className="task-detail-mention-option-email">{option.member.email}</span>
                      )}
                    </button>
                  ))}
                </OsScroll>
              </div>,
              anchorRef.current
            )
          : null
      }
    />
  );
}
